import { BookType } from '../types';

export interface DedicationTemplate {
  id: string;
  nameKey: string;
  contentKey: string;
  content: string;
  bookTypes?: BookType[];
}

export interface EpigraphTemplate {
  id: string;
  nameKey: string;
  quoteKey: string;
  quote: string;
  attributionKey: string;
  attribution: string;
  bookTypes?: BookType[];
}

export interface CopyrightTemplate {
  id: string;
  nameKey: string;
  contentKey: string;
  content: string;
  bookTypes?: BookType[];
}

// Dedication templates
export const DEDICATION_TEMPLATES: DedicationTemplate[] = [
  // Fiction
  {
    id: 'dreamers',
    nameKey: 'frontMatter.templates.dedication.dreamers',
    contentKey: 'frontMatter.templates.dedication.dreamersContent',
    content: 'For all dreamers who dare to imagine',
    bookTypes: ['fiction'],
  },
  {
    id: 'storytellers',
    nameKey: 'frontMatter.templates.dedication.storytellers',
    contentKey: 'frontMatter.templates.dedication.storytellersContent',
    content: 'For the storytellers who came before, and those who will follow',
    bookTypes: ['fiction'],
  },
  {
    id: 'readers',
    nameKey: 'frontMatter.templates.dedication.readers',
    contentKey: 'frontMatter.templates.dedication.readersContent',
    content: 'For readers everywhere, who bring stories to life',
    bookTypes: ['fiction'],
  },
  // Non-fiction
  {
    id: 'knowledge_seekers',
    nameKey: 'frontMatter.templates.dedication.knowledgeSeekers',
    contentKey: 'frontMatter.templates.dedication.knowledgeSeekersContent',
    content: 'For the curious minds seeking knowledge',
    bookTypes: ['non_fiction'],
  },
  {
    id: 'learners',
    nameKey: 'frontMatter.templates.dedication.learners',
    contentKey: 'frontMatter.templates.dedication.learnersContent',
    content: 'To all who never stop learning',
    bookTypes: ['non_fiction'],
  },
  {
    id: 'teachers',
    nameKey: 'frontMatter.templates.dedication.teachers',
    contentKey: 'frontMatter.templates.dedication.teachersContent',
    content: 'For the teachers who inspire us to grow',
    bookTypes: ['non_fiction'],
  },
  // Universal
  {
    id: 'family',
    nameKey: 'frontMatter.templates.dedication.family',
    contentKey: 'frontMatter.templates.dedication.familyContent',
    content: 'For my family, with love',
  },
  {
    id: 'friends',
    nameKey: 'frontMatter.templates.dedication.friends',
    contentKey: 'frontMatter.templates.dedication.friendsContent',
    content: 'For friends who believed in this journey',
  },
  {
    id: 'you',
    nameKey: 'frontMatter.templates.dedication.you',
    contentKey: 'frontMatter.templates.dedication.youContent',
    content: 'For you, dear reader',
  },
];

// Epigraph templates
export const EPIGRAPH_TEMPLATES: EpigraphTemplate[] = [
  // Fiction
  {
    id: 'storytelling',
    nameKey: 'frontMatter.templates.epigraph.storytelling',
    quoteKey: 'frontMatter.templates.epigraph.storytellingQuote',
    quote:
      "A story is a letter the author writes to themselves, to find out things they didn't know they knew.",
    attributionKey: 'frontMatter.templates.epigraph.storytellingAttribution',
    attribution: 'Adapted from traditional wisdom',
    bookTypes: ['fiction'],
  },
  {
    id: 'imagination',
    nameKey: 'frontMatter.templates.epigraph.imagination',
    quoteKey: 'frontMatter.templates.epigraph.imaginationQuote',
    quote: 'Imagination is the only weapon in the war against reality.',
    attributionKey: 'frontMatter.templates.epigraph.imaginationAttribution',
    attribution: 'Lewis Carroll',
    bookTypes: ['fiction'],
  },
  {
    id: 'worlds',
    nameKey: 'frontMatter.templates.epigraph.worlds',
    quoteKey: 'frontMatter.templates.epigraph.worldsQuote',
    quote:
      'A reader lives a thousand lives before he dies. The man who never reads lives only one.',
    attributionKey: 'frontMatter.templates.epigraph.worldsAttribution',
    attribution: 'George R.R. Martin',
    bookTypes: ['fiction'],
  },
  // Non-fiction
  {
    id: 'learning',
    nameKey: 'frontMatter.templates.epigraph.learning',
    quoteKey: 'frontMatter.templates.epigraph.learningQuote',
    quote:
      "The more that you read, the more things you will know. The more that you learn, the more places you'll go.",
    attributionKey: 'frontMatter.templates.epigraph.learningAttribution',
    attribution: 'Dr. Seuss',
    bookTypes: ['non_fiction'],
  },
  {
    id: 'knowledge',
    nameKey: 'frontMatter.templates.epigraph.knowledge',
    quoteKey: 'frontMatter.templates.epigraph.knowledgeQuote',
    quote: 'The only true wisdom is in knowing you know nothing.',
    attributionKey: 'frontMatter.templates.epigraph.knowledgeAttribution',
    attribution: 'Socrates',
    bookTypes: ['non_fiction'],
  },
  {
    id: 'curiosity',
    nameKey: 'frontMatter.templates.epigraph.curiosity',
    quoteKey: 'frontMatter.templates.epigraph.curiosityQuote',
    quote: 'I have no special talents. I am only passionately curious.',
    attributionKey: 'frontMatter.templates.epigraph.curiosityAttribution',
    attribution: 'Albert Einstein',
    bookTypes: ['non_fiction'],
  },
  // Universal
  {
    id: 'beginning',
    nameKey: 'frontMatter.templates.epigraph.beginning',
    quoteKey: 'frontMatter.templates.epigraph.beginningQuote',
    quote: "Every new beginning comes from some other beginning's end.",
    attributionKey: 'frontMatter.templates.epigraph.beginningAttribution',
    attribution: 'Seneca',
  },
  {
    id: 'journey',
    nameKey: 'frontMatter.templates.epigraph.journey',
    quoteKey: 'frontMatter.templates.epigraph.journeyQuote',
    quote: 'A journey of a thousand miles begins with a single step.',
    attributionKey: 'frontMatter.templates.epigraph.journeyAttribution',
    attribution: 'Lao Tzu',
  },
];

// Copyright disclaimer templates
export const COPYRIGHT_TEMPLATES: CopyrightTemplate[] = [
  {
    id: 'fiction_standard',
    nameKey: 'frontMatter.templates.copyright.fictionStandard',
    contentKey: 'frontMatter.templates.copyright.fictionStandardContent',
    content:
      "This is a work of fiction. Names, characters, places, and incidents either are products of the author's imagination or are used fictitiously. Any resemblance to actual persons, living or dead, events, or locales is entirely coincidental.",
    bookTypes: ['fiction'],
  },
  {
    id: 'nonfiction_standard',
    nameKey: 'frontMatter.templates.copyright.nonfictionStandard',
    contentKey: 'frontMatter.templates.copyright.nonfictionStandardContent',
    content:
      'The information in this book is provided for educational and informational purposes only. While the author has made every effort to ensure accuracy, the author makes no warranties about the completeness, reliability, or accuracy of this information.',
    bookTypes: ['non_fiction'],
  },
  {
    id: 'all_rights',
    nameKey: 'frontMatter.templates.copyright.allRights',
    contentKey: 'frontMatter.templates.copyright.allRightsContent',
    content:
      'All rights reserved. No part of this publication may be reproduced, distributed, or transmitted in any form or by any means, including photocopying, recording, or other electronic or mechanical methods, without the prior written permission of the author.',
  },
  {
    id: 'ai_assisted',
    nameKey: 'frontMatter.templates.copyright.aiAssisted',
    contentKey: 'frontMatter.templates.copyright.aiAssistedContent',
    content:
      'This work was created by the author. Digital writing and research tools, including AI-based assistants, were used as supportive instruments during the writing process. All creative direction and final decisions were made by the author.',
  },
  {
    id: 'minimal',
    nameKey: 'frontMatter.templates.copyright.minimal',
    contentKey: 'frontMatter.templates.copyright.minimalContent',
    content: 'All rights reserved.',
  },
];

// Author's Note templates
export interface AuthorsNoteTemplate {
  id: string;
  nameKey: string;
  contentKey: string;
  content: string;
  bookTypes?: BookType[];
}

export const AUTHORS_NOTE_TEMPLATES: AuthorsNoteTemplate[] = [
  {
    id: 'creative_responsibility',
    nameKey: 'frontMatter.templates.authorsNote.creativeResponsibility',
    contentKey: 'frontMatter.templates.authorsNote.creativeResponsibilityContent',
    content:
      'This book reflects my perspective and creative decisions. I retain full responsibility for its content.',
  },
  {
    id: 'personal_journey',
    nameKey: 'frontMatter.templates.authorsNote.personalJourney',
    contentKey: 'frontMatter.templates.authorsNote.personalJourneyContent',
    content:
      'Writing this book has been a deeply personal journey. The ideas, stories, and perspectives within these pages are my own, shaped by my experiences and imagination.',
  },
  {
    id: 'research_based',
    nameKey: 'frontMatter.templates.authorsNote.researchBased',
    contentKey: 'frontMatter.templates.authorsNote.researchBasedContent',
    content:
      'This work is the result of extensive research and careful consideration. While I have strived for accuracy, any errors or omissions are my own.',
    bookTypes: ['non_fiction'],
  },
];

// Transparency Statement templates
export interface TransparencyTemplate {
  id: string;
  nameKey: string;
  contentKey: string;
  content: string;
}

export const TRANSPARENCY_TEMPLATES: TransparencyTemplate[] = [
  {
    id: 'writing_tools',
    nameKey: 'frontMatter.templates.transparency.writingTools',
    contentKey: 'frontMatter.templates.transparency.writingToolsContent',
    content:
      'Writing tools were used to support clarity, structure, and research. All creative direction and final decisions were made by the author.',
  },
  {
    id: 'ai_assistance',
    nameKey: 'frontMatter.templates.transparency.aiAssistance',
    contentKey: 'frontMatter.templates.transparency.aiAssistanceContent',
    content:
      'Digital writing and research tools, including AI-based assistants, were used as supportive instruments during the writing process. The author retains full creative control and responsibility for all content.',
  },
];

// Helper functions to filter templates by book type
export function getDedicationTemplates(bookType?: BookType): DedicationTemplate[] {
  if (!bookType) return DEDICATION_TEMPLATES;
  return DEDICATION_TEMPLATES.filter((t) => !t.bookTypes || t.bookTypes.includes(bookType));
}

export function getEpigraphTemplates(bookType?: BookType): EpigraphTemplate[] {
  if (!bookType) return EPIGRAPH_TEMPLATES;
  return EPIGRAPH_TEMPLATES.filter((t) => !t.bookTypes || t.bookTypes.includes(bookType));
}

export function getCopyrightTemplates(bookType?: BookType): CopyrightTemplate[] {
  if (!bookType) return COPYRIGHT_TEMPLATES;
  return COPYRIGHT_TEMPLATES.filter((t) => !t.bookTypes || t.bookTypes.includes(bookType));
}

// Get template by ID
export function getDedicationTemplate(id: string): DedicationTemplate | undefined {
  return DEDICATION_TEMPLATES.find((t) => t.id === id);
}

export function getEpigraphTemplate(id: string): EpigraphTemplate | undefined {
  return EPIGRAPH_TEMPLATES.find((t) => t.id === id);
}

export function getCopyrightTemplate(id: string): CopyrightTemplate | undefined {
  return COPYRIGHT_TEMPLATES.find((t) => t.id === id);
}

export function getAuthorsNoteTemplates(bookType?: BookType): AuthorsNoteTemplate[] {
  if (!bookType) return AUTHORS_NOTE_TEMPLATES;
  return AUTHORS_NOTE_TEMPLATES.filter((t) => !t.bookTypes || t.bookTypes.includes(bookType));
}

export function getAuthorsNoteTemplate(id: string): AuthorsNoteTemplate | undefined {
  return AUTHORS_NOTE_TEMPLATES.find((t) => t.id === id);
}

export function getTransparencyTemplates(): TransparencyTemplate[] {
  return TRANSPARENCY_TEMPLATES;
}

export function getTransparencyTemplate(id: string): TransparencyTemplate | undefined {
  return TRANSPARENCY_TEMPLATES.find((t) => t.id === id);
}
